import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Chip,
  TableSortLabel,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Users, Moon, Church, Champagne, MagnifyingGlass, Pencil, Trash, EnvelopeSimple } from '@phosphor-icons/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import useThemeStyles from '../../theme/useThemeStyles';
import { usePlatformAlerts } from '../../context/PlatformAlertsContext';
import CoupleMemberGuestModal from './CoupleMemberGuestModal';

function GuestList() {
  const styles = useThemeStyles();
  const { showError, showSuccess } = usePlatformAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [weddingDetails, setWeddingDetails] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [filterType, setFilterType] = useState('both'); // 'ceremony', 'reception', 'both'
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [coupleModalOpen, setCoupleModalOpen] = useState(false);
  const [selectedCoupleGuest, setSelectedCoupleGuest] = useState(null);
  const [guestForm, setGuestForm] = useState({
    name: '',
    email: '',
    phone: '',
    status: 'pending', // pending, confirmed, declined
    plusOne: false,
    invitedToCeremony: true,
    invitedToReception: true,
    invitedToEvening: false,
    dietaryRequirements: '',
    group: 'friend', // friend, family, colleague
  });

  useEffect(() => {
    fetchGuests();
    fetchWeddingDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, []);

  // Open guest modal when guestId is in URL (e.g. from search)
  useEffect(() => {
    const guestId = searchParams.get('guestId');
    if (!guestId || !guests.length) return;
    const guest = guests.find((g) => g._id === guestId);
    if (guest) {
      handleEdit(guest);
      searchParams.delete('guestId');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [searchParams, guests]);

  const fetchWeddingDetails = async () => {
    try {
      const response = await api.get('/api/wedding-details');
      setWeddingDetails(response.data);
    } catch (error) {}
  };

  const fetchGuests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/guests');
      setGuests(response.data);
    } catch (error) {
      showError('Failed to fetch guest list');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingGuest(null);
    setGuestForm({
      name: '',
      email: '',
      phone: '',
      status: 'pending',
      plusOne: false,
      invitedToCeremony: true,
      invitedToReception: true,
      invitedToEvening: false,
      dietaryRequirements: '',
      group: 'friend',
    });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGuest(null);
  };

  const handleEdit = (guest) => {
    if (guest.partnerRef) {
      setSelectedCoupleGuest(guest);
      setCoupleModalOpen(true);
      return;
    }
    setEditingGuest(guest);
    setGuestForm({
      name: guest.name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      status: guest.status || 'pending',
      plusOne: guest.plusOne || false,
      invitedToCeremony: guest.invitedToCeremony !== undefined ? guest.invitedToCeremony : true,
      invitedToReception: guest.invitedToReception !== undefined ? guest.invitedToReception : true,
      invitedToEvening: guest.invitedToEvening !== undefined ? guest.invitedToEvening : false,
      dietaryRequirements: guest.dietaryRequirements || '',
      group: guest.group || 'friend',
    });
    setOpen(true);
  };

  const handleDelete = async (guestId) => {
    try {
      await api.delete(`/api/guests/${guestId}`);
      setGuests(guests.filter(guest => guest._id !== guestId));
      showSuccess('Guest deleted successfully');
    } catch (error) {
      showError('Failed to delete guest');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure boolean fields are explicitly set as booleans
      const submitData = {
        name: guestForm.name,
        email: guestForm.email,
        phone: guestForm.phone || '',
        status: guestForm.status || 'pending',
        plusOne: Boolean(guestForm.plusOne),
        invitedToCeremony: Boolean(guestForm.invitedToCeremony),
        invitedToReception: Boolean(guestForm.invitedToReception),
        invitedToEvening: Boolean(guestForm.invitedToEvening),
        dietaryRequirements: guestForm.dietaryRequirements || '',
        group: guestForm.group || 'friend'
      };

      if (editingGuest) {
        const response = await api.put(`/api/guests/${editingGuest._id}`, submitData);
        setGuests(guests.map(g => g._id === editingGuest._id ? response.data : g));
      } else {
        const response = await api.post('/api/guests', submitData);
        setGuests([...guests, response.data]);
      }
      showSuccess(`Guest ${editingGuest ? 'updated' : 'added'} successfully`);
      handleClose();
    } catch (error) {
      showError(`Failed to ${editingGuest ? 'update' : 'add'} guest: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setGuestForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const sendRSVPEmail = async (guestId) => {
    try {
      await api.post(`/api/rsvp/send-email/${guestId}`);
      showSuccess('RSVP email sent successfully');
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        error.message ||
        'Failed to send RSVP email';
      showError(msg);
    }
  };

  // Anyone yes to ceremony or reception is automatically yes to evening for display/count
  const isEveningGuest = (g) =>
    g.invitedToCeremony !== false || g.invitedToReception !== false || g.invitedToEvening === true;

  // Calculate counts for each filter type
  const counts = useMemo(() => {
    if (!guests || guests.length === 0) {
      return { ceremony: 0, reception: 0, receptionOnly: 0, evening: 0, both: 0 };
    }
    
    const ceremonyCount = guests.filter(g => g.invitedToCeremony !== false && !g.invitedToEvening).length;
    const receptionOnlyCount = guests.filter(g => g.invitedToReception !== false && !g.invitedToEvening).length;
    const eveningCount = guests.filter(g => isEveningGuest(g)).length;
    // Total reception count includes both reception guests and evening guests (for capacity purposes)
    const receptionCount = receptionOnlyCount + eveningCount;
    const bothCount = guests.length;
    
    return { ceremony: ceremonyCount, reception: receptionCount, receptionOnly: receptionOnlyCount, evening: eveningCount, both: bothCount };
  }, [guests]);

  // Get capacities from wedding details
  const ceremonyCapacity = weddingDetails?.venues?.ceremony?.capacity;
  const receptionCapacity = weddingDetails?.venues?.reception?.capacity;
  const eveningCapacity = weddingDetails?.venues?.reception?.eveningCapacity;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredGuests = useMemo(() => {
    const coupleFirst = (a, b) => {
      const aPartner = a.partnerRef ? (a.partnerRef === 'partnerOne' ? 0 : 1) : 2;
      const bPartner = b.partnerRef ? (b.partnerRef === 'partnerOne' ? 0 : 1) : 2;
      if (aPartner !== bPartner) return aPartner - bPartner;
      return 0;
    };

    let filtered = guests.filter(guest => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        guest.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.phone?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;

      // Invitation type filter
      if (filterType === 'ceremony') {
        return guest.invitedToCeremony !== false && !guest.invitedToEvening;
      } else if (filterType === 'reception') {
        return guest.invitedToReception !== false && !guest.invitedToEvening;
      } else if (filterType === 'evening') {
        return isEveningGuest(guest);
      } else {
        // 'both' - show all guests
        return true;
      }
    });

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const partnerCompare = coupleFirst(a, b);
        if (partnerCompare !== 0) return partnerCompare;

        let aValue, bValue;
        switch (sortField) {
          case 'ceremony':
            aValue = a.invitedToCeremony !== false && !a.invitedToEvening ? 1 : 0;
            bValue = b.invitedToCeremony !== false && !b.invitedToEvening ? 1 : 0;
            break;
          case 'reception':
            aValue = a.invitedToReception !== false && !a.invitedToEvening ? 1 : 0;
            bValue = b.invitedToReception !== false && !b.invitedToEvening ? 1 : 0;
            break;
          case 'evening':
            aValue = isEveningGuest(a) ? 1 : 0;
            bValue = isEveningGuest(b) ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (sortDirection === 'asc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      });
    } else {
      filtered = [...filtered].sort(coupleFirst);
    }

    return filtered;
  }, [guests, filterType, sortField, sortDirection, searchTerm]);

  const getCapacityColor = (count, capacity) => {
    if (!capacity) return 'default';
    const percentage = (count / capacity) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 85) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={styles.patterns.pageHeader}>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          Guest List
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpen}
        >
          Add Guest
        </Button>
      </Box>

      {/* Count and Capacity Cards - Clickable Filters */}
      <Grid container spacing={2} sx={{ mb: { xs: 3, sm: 2 } }}>
        <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
          <Card 
            sx={{ 
              ...styles.card.default, 
              flex: 1,
              display: 'flex',
              bgcolor: filterType === 'both' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('both')}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Total Guests
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {counts.both}
                  </Typography>
                </Box>
                <Users size={32} style={{ color: '#64748b', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{ 
              ...styles.card.default, 
              bgcolor: filterType === 'ceremony' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('ceremony')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Ceremony Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Church size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {counts.ceremony}
                    </Typography>
                  </Box>
                  {ceremonyCapacity != null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Capacity:
                        </Typography>
                        <Chip
                          label={`${counts.ceremony} / ${ceremonyCapacity}`}
                          size="small"
                          color={getCapacityColor(counts.ceremony, ceremonyCapacity)}
                          variant="outlined"
                        />
                      </Box>
                      {ceremonyCapacity === 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                          onClick={() => navigate('/wedding-details?section=venues&highlight=ceremonyCapacity')}
                        >
                          Update capacity
                        </Button>
                      )}
                    </Box>
                  )}
                  {ceremonyCapacity == null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Capacity: {counts.ceremony} / No capacity set
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 600 }}
                        onClick={() => navigate('/wedding-details?section=venues&highlight=ceremonyCapacity')}
                      >
                        Update capacity
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Card 
            sx={{ 
              ...styles.card.default, 
              bgcolor: filterType === 'reception' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('reception')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Reception Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Champagne size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {counts.receptionOnly}
                    </Typography>
                  </Box>
                  {receptionCapacity != null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Capacity:
                        </Typography>
                        <Chip
                          label={`${counts.receptionOnly} / ${receptionCapacity}`}
                          size="small"
                          color={getCapacityColor(counts.receptionOnly, receptionCapacity)}
                          variant="outlined"
                        />
                      </Box>
                      {receptionCapacity === 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                          onClick={() => navigate('/wedding-details?section=venues&highlight=receptionCapacity')}
                        >
                          Update capacity
                        </Button>
                      )}
                    </Box>
                  )}
                  {receptionCapacity == null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Capacity: {counts.receptionOnly} / No capacity set
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 600 }}
                        onClick={() => navigate('/wedding-details?section=venues&highlight=receptionCapacity')}
                      >
                        Update capacity
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            sx={{
              ...styles.card.default,
              bgcolor: filterType === 'evening' ? 'action.selected' : 'background.paper',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
            onClick={() => setFilterType('evening')}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    Evening Guests
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Moon size={24} style={{ color: '#64748b' }} />
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      {counts.evening}
                    </Typography>
                  </Box>
                  {eveningCapacity != null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          Capacity:
                        </Typography>
                        <Chip
                          label={`${counts.evening} / ${eveningCapacity}`}
                          size="small"
                          color={getCapacityColor(counts.evening, eveningCapacity)}
                          variant="outlined"
                        />
                      </Box>
                      {eveningCapacity === 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 0.5, fontWeight: 600 }}
                          onClick={() => navigate('/wedding-details?section=venues&highlight=eveningCapacity')}
                        >
                          Update capacity
                        </Button>
                      )}
                    </Box>
                  )}
                  {eveningCapacity == null && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Capacity: {counts.evening} / No capacity set
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 600 }}
                        onClick={() => navigate('/wedding-details?section=venues&highlight=eveningCapacity')}
                      >
                        Update capacity
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search guests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={20} />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {sortedAndFilteredGuests.length === 0 ? (
        <Card sx={styles.card.default}>
          <CardContent>
            <Box sx={styles.patterns.emptyState}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm ? 'No guests found matching your search' : 'No guests added yet'}
              </Typography>
              {!searchTerm && (
                <>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Start by adding your first guest to the list
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpen}
                  >
                    Add First Guest
                  </Button>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={styles.card.default}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Tooltip title="Sort by Ceremony">
                      <TableSortLabel
                        active={sortField === 'ceremony'}
                        direction={sortField === 'ceremony' ? sortDirection : 'asc'}
                        onClick={() => handleSort('ceremony')}
                      >
                        <Church size={18} />
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sort by Reception">
                      <TableSortLabel
                        active={sortField === 'reception'}
                        direction={sortField === 'reception' ? sortDirection : 'asc'}
                        onClick={() => handleSort('reception')}
                      >
                        <Champagne size={18} />
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Sort by Evening">
                      <TableSortLabel
                        active={sortField === 'evening'}
                        direction={sortField === 'evening' ? sortDirection : 'asc'}
                        onClick={() => handleSort('evening')}
                      >
                        <Moon size={18} />
                      </TableSortLabel>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedAndFilteredGuests.map((guest) => (
                  <TableRow key={guest._id}>
                    <TableCell>{guest.name}</TableCell>
                    <TableCell>{guest.email}</TableCell>
                    <TableCell>{guest.phone}</TableCell>
                    <TableCell>{guest.status}</TableCell>
                    <TableCell>
                      {guest.invitedToCeremony !== false && !guest.invitedToEvening ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      {guest.invitedToReception !== false && !guest.invitedToEvening ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      {isEveningGuest(guest) ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      {guest.partnerRef ? (
                        <Chip label="Couple" size="small" color="primary" variant="outlined" />
                      ) : (
                        guest.group
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(guest)}>
                        <Pencil size={18} />
                      </IconButton>
                      {!guest.partnerRef && (
                        <>
                          <IconButton onClick={() => handleDelete(guest._id)}>
                            <Trash size={18} />
                          </IconButton>
                          <IconButton onClick={() => sendRSVPEmail(guest._id)}>
                            <EnvelopeSimple size={18} />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGuest ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
        <DialogContent>
          <Box sx={styles.patterns.formContainer} mt={2}>
            <TextField
              fullWidth
              label="Name"
              value={guestForm.name}
              onChange={handleChange('name')}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={guestForm.email}
              onChange={handleChange('email')}
            />
            <TextField
              fullWidth
              label="Phone"
              value={guestForm.phone}
              onChange={handleChange('phone')}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={guestForm.status}
                onChange={handleChange('status')}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="declined">Declined</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Group</InputLabel>
              <Select
                value={guestForm.group}
                onChange={handleChange('group')}
                label="Group"
              >
                <MenuItem value="friend">Friend</MenuItem>
                <MenuItem value="family">Family</MenuItem>
                <MenuItem value="colleague">Colleague</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={guestForm.invitedToCeremony}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setGuestForm(prev => ({
                      ...prev,
                      invitedToCeremony: checked,
                      // If ceremony is checked, uncheck evening
                      invitedToEvening: checked ? false : prev.invitedToEvening
                    }));
                  }}
                  disabled={guestForm.invitedToEvening}
                />
              }
              label="Invited to Ceremony"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={guestForm.invitedToReception}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setGuestForm(prev => ({
                      ...prev,
                      invitedToReception: checked,
                      // If reception is checked, uncheck evening
                      invitedToEvening: checked ? false : prev.invitedToEvening
                    }));
                  }}
                  disabled={guestForm.invitedToEvening}
                />
              }
              label="Invited to Reception"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={guestForm.invitedToEvening}
                  onChange={(e) => {
                    const isEvening = e.target.checked;
                    setGuestForm(prev => ({
                      ...prev,
                      invitedToEvening: isEvening,
                      // If evening guest, set ceremony and reception to false
                      invitedToCeremony: isEvening ? false : prev.invitedToCeremony,
                      invitedToReception: isEvening ? false : prev.invitedToReception
                    }));
                  }}
                />
              }
              label="Evening Guest Only"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={guestForm.plusOne}
                  onChange={handleChange('plusOne')}
                />
              }
              label="Has Plus One"
            />
            <TextField
              fullWidth
              label="Dietary Requirements"
              multiline
              rows={2}
              value={guestForm.dietaryRequirements}
              onChange={handleChange('dietaryRequirements')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingGuest ? 'Update' : 'Add'} Guest
          </Button>
        </DialogActions>
      </Dialog>

      <CoupleMemberGuestModal
        open={coupleModalOpen}
        onClose={() => {
          setCoupleModalOpen(false);
          setSelectedCoupleGuest(null);
        }}
        guest={selectedCoupleGuest}
        onSaved={() => fetchGuests()}
      />
    </Box>
  );
}

export default GuestList; 